import {
  AccessAlarmsSharp as AccessAlarmsSharpIcon,
  LoopSharp as LoopSharpIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  SettingsInputComposite,
  StorageSharp as StorageSharpIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Drawer,
  FormControl,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import Alert from 'components/features/alert/Alert';
import AppAvatar from 'components/features/avatar/Avatar';
import PrettoSlider from 'components/features/input/PrettoSlider';
import AppTextField from 'components/features/input/TextField';
import AppLoading from 'components/features/loading/AppLoading';
import AppModal from 'components/features/modal/Modal';
import { API_PATHS } from 'constant/api';
import { PAGE_LIST } from 'constant/pages';
import i18next from 'i18next';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAbsenceTime,
  getFaceIds,
  getThreshold,
  postAbsenceTime,
  postSettingDb,
  postThreshold,
} from 'utils/fetch';
import ja from 'utils/ja';

const overridesGrid: Record<number, { cols: number; rows: number }> = {
  1: { cols: 1, rows: 1 },
  2: { cols: 2, rows: 1 },
  3: { cols: 3, rows: 1 },
  4: { cols: 2, rows: 2 },
  6: { cols: 3, rows: 2 },
  8: { cols: 4, rows: 2 },
  9: { cols: 3, rows: 3 },
  10: { cols: 5, rows: 2 },
  12: { cols: 4, rows: 3 },
  14: { cols: 7, rows: 2 },
  15: { cols: 5, rows: 3 },
  16: { cols: 4, rows: 4 },
  18: { cols: 6, rows: 3 },
  20: { cols: 5, rows: 4 },
  21: { cols: 7, rows: 3 },
  24: { cols: 6, rows: 4 },
  25: { cols: 5, rows: 5 },
  27: { cols: 9, rows: 3 },
  28: { cols: 7, rows: 4 },
  30: { cols: 6, rows: 5 },
  32: { cols: 8, rows: 4 },
  35: { cols: 7, rows: 5 },
  36: { cols: 6, rows: 6 },
  40: { cols: 8, rows: 5 },
};

const HomePage: FC = () => {
  const theme = useTheme();

  const [openMenu, setOpenMenu] = useState(false);

  const [openModalSettingDb, setOpenModalSettingDb] = useState(false);

  const [openModalReset, setOpenModalReset] = useState(false);

  const [openModalThreshold, setOpenModalThreshold] = useState(false);

  const [openModalChangeDb, setOpenModalChangeDb] = useState(false);

  const [openModalChangeAbsence, setOpenModalChangeAbsence] = useState(false);


  const [faceIds, setFaceIds] = useState<IFaceIdsRes[]>([]);

  const urlGetAvatar = `${process.env.REACT_APP_USER_API_URL}${API_PATHS.getAvatarImage}`;
  const navigate = useNavigate();

  const [absenceTimeValue, setAbsenceTimeValue] = useState<number | null>(null);

  const [absenceTimeValueTmp, setAbsenceTimeValueTmp] = useState<number | null>(
    null
  );

  const [thresholdValue, setThresholdValue] = useState<number | null>(null);

  const [thresholdValueTmp, setThresholdValueTmp] = useState<number | null>(
    null
  );

  const [loading, setLoading] = useState<boolean>(false);

  const [configError, setConfigError] = useState<boolean>(false);

  const [keyConfig, setKeyConfig] = useState<IKeyFormInput>({
    privateDbId: { value: '', error: false },
    privateDbKey: { value: '', error: false },
  });

  const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.id as keyof IKeyFormInput;
    setKeyConfig((oldState) => ({
      ...oldState,
      [key]: {
        value: event.target.value.trim(),
        error: !event.target.value.trim(),
      },
    }));
  };

  const handleRightSettingDb = () => {
    localStorage.removeItem('accessToken');
    setOpenModalSettingDb(false);
    navigate(PAGE_LIST.SETTING.pathname(), { replace: true });
  };

  const handlePostAbsenceTime = async () => {
    try {
      setLoading(true);
      await postAbsenceTime({ absenceTime: absenceTimeValueTmp ?? 0 });
      setOpenModalChangeAbsence(false);
      handleGetAbsenceTime();
    } catch (error) {
      setConfigError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGetAbsenceTime = async () => {
    try {
      const { data } = await getAbsenceTime();
      if (data) {
        setAbsenceTimeValue(Number(data));
      }
    } catch (error) {
      // empty
    }
  };

  useEffect(() => {
    handleGetAbsenceTime();
  }, []);

  const handlePostThreshold = async () => {
    try {
      setLoading(true);
      await postThreshold({ threshold: thresholdValueTmp ?? 0 });
      setOpenModalThreshold(false);
      handleGetThreshold();
    } catch (error) {
      setConfigError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGetThreshold = async () => {
    try {
      const { data } = await getThreshold();
      setThresholdValue(Number(data));
    } catch (error) {
      //empty
    }
  };

  const handleSliderChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    const value = newValue as number;
    setThresholdValueTmp(value);
  };

  useEffect(() => {
    handleGetThreshold();
  }, []);

  const menuItems = [
    {
      text: ja.home.reset,
      icon: (
        <LoopSharpIcon
          sx={{
            color: theme.palette.common.yellow,
            width: 40,
            height: 40,
          }}
        />
      ),
      onClick: () => {
        setOpenModalReset(true);
      },
    },
    {
      text: i18next.t(ja.home.timeMinutes, { 0: absenceTimeValue }),
      icon: (
        <AccessAlarmsSharpIcon
          sx={{
            color: theme.palette.primary.main,
            width: 40,
            height: 40,
          }}
        />
      ),
      onClick: () => {
        setAbsenceTimeValueTmp(absenceTimeValue);
        setOpenModalChangeAbsence(true);
      },
    },
    {
      text: ja.home.setDb,
      icon: (
        <StorageSharpIcon
          sx={{
            color: theme.palette.common.green,
            width: 40,
            height: 40,
          }}
        />
      ),
      onClick: () => {
        setKeyConfig({
          privateDbId: { value: localStorage.getItem('privateDbId') || "", error: false },
          privateDbKey: { value: localStorage.getItem('privateDbKey') || "", error: false },
        })
        setOpenModalChangeDb(true);
      },
    },
    {
      text: ja.home.settingDb,
      icon: (
        <SettingsIcon
          sx={{
            color: theme.palette.common.red,
            width: 40,
            height: 40,
          }}
        />
      ),
      onClick: () => {
        setOpenModalSettingDb(true);
      },
    },
    {
      text: i18next.t(ja.home.thresholdValue, { 0: thresholdValue }),
      icon: (
        <SettingsInputComposite
          sx={{
            color: theme.palette.common.purple,
            width: 40,
            height: 40,
          }}
        />
      ),
      onClick: () => {
        setThresholdValueTmp(thresholdValue);
        setOpenModalThreshold(true);
      },
    },
  ];

  const handleChangeDb = async () => {
    if (keyConfig.privateDbId.value && keyConfig.privateDbKey.value) {
      try {
        setLoading(true);
        const data = await postSettingDb({
          privateDbId: keyConfig.privateDbId.value,
          privateDbKey: keyConfig.privateDbKey.value,
        });
        if (!data) {
          setConfigError(true);
        } else {
          setOpenModalChangeDb(false);
          localStorage.setItem('privateDbId', keyConfig.privateDbId.value);
          localStorage.setItem('privateDbKey', keyConfig.privateDbKey.value);
        }
      } catch (error) {
        setConfigError(true);
      } finally {
        setLoading(false);
      }
    } else {
      setKeyConfig({
        privateDbId: {
          value: keyConfig.privateDbId.value,
          error: !keyConfig.privateDbId.value,
        },
        privateDbKey: {
          value: keyConfig.privateDbKey.value,
          error: !keyConfig.privateDbKey.value,
        },
      });
    }
  };

  const handleGetFaceIds = async () => {
    try {
      const result = await getFaceIds();
      setFaceIds(result.data);
    } catch (error) {
      //empty
    }
  };

  useEffect(() => {
    handleGetFaceIds();
    const interval = setInterval(() => {
      handleGetFaceIds();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    handleGetFaceIds();
  }, [keyConfig.privateDbId]);

  useEffect(() => {
    handleGetThreshold();
  }, [keyConfig.privateDbId]);

  useEffect(() => {
    handleGetThreshold();
  }, [keyConfig.privateDbId]);

  const calculateGridDimensions = (totalItems: number) => {
    if (totalItems <= 0) return { cols: 1, rows: 1 };

    if (overridesGrid[totalItems]) {
      return overridesGrid[totalItems];
    }

    const cols = Math.ceil(Math.sqrt(totalItems));
    const rows = Math.ceil(totalItems / cols);
    return { cols, rows };
  };

  const { cols, rows } = calculateGridDimensions(faceIds.length);

  return (
    <Box sx={{ position: 'relative', background: theme.palette.common.black }}>
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
        }}
      >
        {Array.from({ length: faceIds.length }).map((_, index) => (
          <Box
            key={index}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <AppAvatar
              src={`${urlGetAvatar}/${faceIds[index].faceId}`}
              isAbsent={faceIds[index].isAbsent}
              alt={faceIds[index].displayName}
            />
          </Box>
        ))}
      </Box>
      {!openMenu && (
        <Box
          sx={{
            position: 'fixed',
            width: 60,
            height: 60,
            top: '20%',
            transform: 'translateY(-50%)',
            backgroundColor: alpha('#1C2434', 0.8),
            border: '1px solid #333A48',
            borderRadius: '8px',
            p: 1,
            cursor: 'pointer',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton onClick={() => setOpenMenu(true)}>
            <MenuIcon
              sx={{
                color: theme.palette.common.white,
                width: 50,
                height: 50,
              }}
            />
          </IconButton>
        </Box>
      )}
      <Drawer anchor='left' open={openMenu} onClose={() => setOpenMenu(false)}>
        <Box
          sx={{
            width: 300,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Box
            component='img'
            src='./images/logo.png'
            alt='Logo'
            sx={{
              height: { xs: '20px', sm: '24px', md: '28px' },
              flexShrink: 0,
              my: '20px',
            }}
          />
          {menuItems.map((menu, index) => (
            <Box
              key={index}
              sx={{
                '&:hover': { cursor: 'pointer' },
                width: '100%',
                height: 50,
                border: `1px solid ${theme.palette.secondary.main}`,
                borderRadius: '4px',
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignContent: 'center',
                alignItems: 'center',
                padding: '0 5px 0 5px',
              }}
              onClick={() => {
                setOpenMenu(false);
                menu.onClick();
              }}
            >
              {menu.icon}
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                }}
              >
                {menu.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Drawer>
      <AppModal
        chargerId={ja.home.reset}
        open={openModalReset}
        handleClose={() => setOpenModalReset(false)}
        handleRight={() => {
          if (faceIds.length) {
            setFaceIds((prev) =>
              prev.map((item) => ({
                ...item,
                isAbsent: true,
              }))
            );
          }
          setOpenModalReset(false);
        }}
        title={ja.home.reset}
        content={
          <Typography sx={{ fontSize: '30px' }}>
            {ja.home.contentModalResetData}
          </Typography>
        }
      />
      <AppModal
        chargerId={ja.home.absenceTime}
        open={openModalChangeAbsence}
        handleClose={() => setOpenModalChangeAbsence(false)}
        title={ja.home.absenceTime}
        content={
          <AppTextField
            id='absenceTime'
            title={ja.home.absenceTimeMinutes.label}
            placeholder={ja.home.absenceTimeMinutes.placeholder}
            inputType='number'
            value={
              absenceTimeValueTmp !== null ? String(absenceTimeValueTmp) : ''
            }
            onChange={(e) => {
              const value = Number(e.target.value);
              setAbsenceTimeValueTmp(value > 0 ? value : null);
            }}
            required
          />
        }
        handleRight={handlePostAbsenceTime}
      />
      <AppModal
        chargerId={ja.home.setDb}
        open={openModalChangeDb}
        handleClose={() => setOpenModalChangeDb(false)}
        handleRight={() => handleChangeDb()}
        title={ja.home.setDb}
        content={
          <Box sx={{ '& *': { fontSize: '30px !important' }, width: '100%' }}>
            <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
              <AppTextField
                id='privateDbId'
                placeholder={ja.setting.db_id.placeholder}
                value={keyConfig.privateDbId.value}
                onChange={handleChangeText}
                title={ja.setting.db_id.label}
                required
                error={keyConfig.privateDbId.error}
              />
            </FormControl>

            <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
              <AppTextField
                id='privateDbKey'
                placeholder={ja.setting.db_key.placeholder}
                value={keyConfig.privateDbKey.value}
                onChange={handleChangeText}
                title={ja.setting.db_key.label}
                required
                error={keyConfig.privateDbKey.error}
              />
            </FormControl>
          </Box>
        }
      />
      <AppModal
        chargerId={ja.home.settingDb}
        open={openModalSettingDb}
        handleClose={() => setOpenModalSettingDb(false)}
        title={ja.home.settingDb}
        content={
          <Typography
            sx={{ fontSize: '30px', width: '100%', textAlign: 'center' }}
          >
            {ja.home.contentModalSettingDb}
          </Typography>
        }
        handleRight={handleRightSettingDb}
      />

      <AppModal
        chargerId={ja.home.thresholdValue}
        open={openModalThreshold}
        handleClose={() => setOpenModalThreshold(false)}
        title={ja.home.threshold}
        content={
          <>
            <Box sx={{ '& *': { fontSize: '30px !important' }, width: '100%' }}>
              <Typography component='p' mb='15px'>
                {i18next.t(ja.home.thresholdValue, { 0: thresholdValueTmp })}
                <Typography
                  component='span'
                  color={theme.palette.error.main}
                  ml={1}
                >
                  *
                </Typography>
              </Typography>
            </Box>
            <PrettoSlider
              value={thresholdValueTmp}
              onChange={handleSliderChange}
            />
          </>
        }
        handleRight={handlePostThreshold}
      />
      <AppLoading loading={loading} />
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.key_config}
        type='error'
      />
    </Box>
  );
};

export default HomePage;
